import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Send,
  MapPin,
  Users,
  Globe,
  Clock,
  CheckCircle,
  AlertTriangle,
  MessageCircle,
  Phone,
  Mail,
  Radio,
  Target,
  Filter,
  Volume2,
  Shield,
  Eye
} from 'lucide-react';

interface Alert {
  id: string;
  title: string;
  message: string;
  zone: string;
  severity: 'critical' | 'warning' | 'advisory' | 'info';
  timestamp: string;
  sentBy: string;
  channels: ('sms' | 'email' | 'push' | 'siren')[];
  recipients: number;
  opened: number;
  status: 'active' | 'expired' | 'cancelled';
}

interface Zone {
  id: string;
  name: string;
  population: number;
  devices: number;
  status: 'critical' | 'warning' | 'monitor' | 'safe';
}

const PublicAlertSystem: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([
    {
      id: '1',
      title: 'IMMEDIATE EVACUATION ORDER',
      message: 'Zone A flooding critical. Evacuate to higher ground immediately. Emergency shelters open at City Hall and Community Center.',
      zone: 'Zone A',
      severity: 'critical',
      timestamp: '10:47 AM',
      sentBy: 'Commander Chen',
      channels: ['sms', 'push', 'siren'],
      recipients: 1247,
      opened: 892,
      status: 'active'
    },
    {
      id: '2',
      title: 'Fire Warning - Zone B',
      message: 'Wildfire approaching residential areas. Prepare for possible evacuation. Keep roads clear for emergency vehicles.',
      zone: 'Zone B',
      severity: 'warning',
      timestamp: '9:30 AM',
      sentBy: 'Commander Patel',
      channels: ['sms', 'email', 'push'],
      recipients: 3456,
      opened: 2341,
      status: 'active'
    },
    {
      id: '3',
      title: 'Earthquake Aftershock Advisory',
      message: 'Multiple aftershocks expected in next 24 hours. Stay away from damaged structures. Report any new damage.',
      zone: 'Zone C',
      severity: 'advisory',
      timestamp: 'Yesterday',
      sentBy: 'Commander Kim',
      channels: ['sms', 'push'],
      recipients: 5678,
      opened: 4321,
      status: 'active'
    }
  ]);

  const [zones] = useState<Zone[]>([
    { id: 'A', name: 'Zone A', population: 12470, devices: 8920, status: 'critical' },
    { id: 'B', name: 'Zone B', population: 23450, devices: 15670, status: 'warning' },
    { id: 'C', name: 'Zone C', population: 18900, devices: 12340, status: 'monitor' },
    { id: 'D', name: 'Zone D', population: 34560, devices: 23450, status: 'safe' },
    { id: 'E', name: 'Zone E', population: 15670, devices: 10980, status: 'safe' }
  ]);

  const [showComposeModal, setShowComposeModal] = useState(false);
  const [selectedZone, setSelectedZone] = useState<string>('Zone A');
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'critical' | 'warning' | 'advisory' | 'info'>('warning');
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['sms', 'push']);

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'warning': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'advisory': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'info': return 'text-blue-500 border-blue-500 bg-blue-500/10';
      default: return 'text-gray-500';
    }
  };

  const getZoneStatusColor = (status: string) => {
    switch(status) {
      case 'critical': return 'text-red-500 border-red-500 bg-red-500/10';
      case 'warning': return 'text-orange-500 border-orange-500 bg-orange-500/10';
      case 'monitor': return 'text-yellow-500 border-yellow-500 bg-yellow-500/10';
      case 'safe': return 'text-green-500 border-green-500 bg-green-500/10';
      default: return 'text-gray-500';
    }
  };

  const sendAlert = () => {
    const newAlert: Alert = {
      id: Date.now().toString(),
      title: alertTitle,
      message: alertMessage,
      zone: selectedZone,
      severity: alertSeverity,
      timestamp: 'Just now',
      sentBy: 'Commander',
      channels: selectedChannels as any,
      recipients: zones.find(z => z.name === selectedZone)?.population || 0,
      opened: 0,
      status: 'active'
    };
    
    setAlerts([newAlert, ...alerts]);
    setShowComposeModal(false);
    resetForm();
  };

  const resetForm = () => {
    setAlertTitle('');
    setAlertMessage('');
    setAlertSeverity('warning');
    setSelectedChannels(['sms', 'push']);
  };

  const toggleChannel = (channel: string) => {
    if (selectedChannels.includes(channel)) {
      setSelectedChannels(selectedChannels.filter(c => c !== channel));
    } else {
      setSelectedChannels([...selectedChannels, channel]);
    }
  };

  return (
    <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 holographic-card h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Bell className="text-orange-500 animate-pulse" />
            PUBLIC ALERT SYSTEM
          </h2>
          <p className="text-xs text-gray-400 mt-1">Emergency broadcast to affected populations</p>
        </div>

        <button
          onClick={() => setShowComposeModal(true)}
          className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          <Send size={16} />
          COMPOSE ALERT
        </button>
      </div>

      {/* Zone Status Overview */}
      <div className="mb-6">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <MapPin size={14} className="text-cyan-500" />
          Zone Status
        </h3>
        <div className="grid grid-cols-5 gap-2">
          {zones.map((zone) => (
            <motion.div
              key={zone.id}
              whileHover={{ scale: 1.05 }}
              className={`p-2 rounded-lg text-center border ${getZoneStatusColor(zone.status)}`}
              onClick={() => setSelectedZone(zone.name)}
            >
              <div className="text-xs font-bold">{zone.name}</div>
              <div className="text-[10px] text-gray-400">{zone.population/1000}k people</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Alerts Feed */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scroll">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <Radio size={14} className="text-purple-500" />
          Recent Alerts
        </h3>

        <AnimatePresence>
          {alerts.map((alert) => (
            <motion.div
              key={alert.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="bg-black/30 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className={
                    alert.severity === 'critical' ? 'text-red-500' :
                    alert.severity === 'warning' ? 'text-orange-500' :
                    'text-yellow-500'
                  } />
                  <h4 className="font-bold text-sm">{alert.title}</h4>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${getSeverityColor(alert.severity)}`}>
                  {alert.severity.toUpperCase()}
                </span>
              </div>

              <p className="text-xs text-gray-300 mb-3">{alert.message}</p>

              <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                <div className="flex items-center gap-1">
                  <MapPin size={10} />
                  {alert.zone}
                </div>
                <div className="flex items-center gap-1">
                  <Clock size={10} />
                  {alert.timestamp}
                </div>
                <div className="flex items-center gap-1">
                  <Users size={10} />
                  {alert.recipients.toLocaleString()} recipients
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {alert.channels.map((channel) => (
                    <span key={channel} className="px-2 py-1 bg-gray-800 rounded text-[10px] uppercase">
                      {channel}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <Eye size={12} className="text-gray-400" />
                  <span className="text-xs text-gray-400">
                    {Math.round((alert.opened / alert.recipients) * 100)}% opened
                  </span>
                </div>
              </div>

              {/* Read Rate Bar */}
              <div className="w-full bg-gray-700 h-1 rounded-full mt-3">
                <div
                  className="h-1 bg-green-500 rounded-full"
                  style={{ width: `${(alert.opened / alert.recipients) * 100}%` }}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Compose Alert Modal */}
      <AnimatePresence>
        {showComposeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
            onClick={() => setShowComposeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-gray-900 w-96 p-6 rounded-xl border border-orange-500/50"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-orange-500 mb-4 flex items-center gap-2">
                <Bell size={20} />
                COMPOSE PUBLIC ALERT
              </h3>

              {/* Zone Selection */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Target Zone</label>
                <select
                  value={selectedZone}
                  onChange={(e) => setSelectedZone(e.target.value)}
                  className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                >
                  {zones.map((zone) => (
                    <option key={zone.id} value={zone.name}>{zone.name} - {zone.population.toLocaleString()} people</option>
                  ))}
                </select>
              </div>

              {/* Severity */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Severity Level</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['critical', 'warning', 'advisory', 'info'] as const).map((sev) => (
                    <button
                      key={sev}
                      onClick={() => setAlertSeverity(sev)}
                      className={`py-2 px-3 rounded-lg text-sm font-bold uppercase ${
                        alertSeverity === sev
                          ? sev === 'critical' ? 'bg-red-600 text-white' :
                            sev === 'warning' ? 'bg-orange-600 text-white' :
                            sev === 'advisory' ? 'bg-yellow-600 text-white' : 'bg-blue-600 text-white'
                          : 'bg-gray-800 text-gray-400'
                      }`}
                    >
                      {sev}
                    </button>
                  ))}
                </div>
              </div>

              {/* Alert Title */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Alert Title</label>
                <input
                  type="text"
                  value={alertTitle}
                  onChange={(e) => setAlertTitle(e.target.value)}
                  placeholder="e.g., IMMEDIATE EVACUATION ORDER"
                  className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Alert Message */}
              <div className="mb-4">
                <label className="text-xs text-gray-400 mb-1 block">Alert Message</label>
                <textarea
                  value={alertMessage}
                  onChange={(e) => setAlertMessage(e.target.value)}
                  placeholder="Enter alert details..."
                  rows={3}
                  className="w-full bg-black/50 border border-gray-700 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Delivery Channels */}
              <div className="mb-6">
                <label className="text-xs text-gray-400 mb-2 block">Delivery Channels</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'sms', label: 'SMS', icon: MessageCircle },
                    { id: 'email', label: 'Email', icon: Mail },
                    { id: 'push', label: 'Push', icon: Bell },
                    { id: 'siren', label: 'Siren', icon: Volume2 }
                  ].map((channel) => {
                    const Icon = channel.icon;
                    return (
                      <button
                        key={channel.id}
                        onClick={() => toggleChannel(channel.id)}
                        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${
                          selectedChannels.includes(channel.id)
                            ? 'bg-cyan-600 text-white'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        <Icon size={14} />
                        {channel.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Stats Preview */}
              <div className="p-3 bg-black/30 rounded-lg mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-400">Estimated Recipients:</span>
                  <span className="font-bold text-cyan-500">
                    {zones.find(z => z.name === selectedZone)?.population.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Active Devices:</span>
                  <span className="font-bold text-green-500">
                    {zones.find(z => z.name === selectedZone)?.devices.toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={sendAlert}
                  className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                >
                  <Send size={16} />
                  SEND ALERT
                </button>
                <button
                  onClick={() => setShowComposeModal(false)}
                  className="flex-1 bg-gray-800 hover:bg-gray-700 text-white font-bold py-3 rounded-lg"
                >
                  CANCEL
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Footer */}
      <div className="mt-4 grid grid-cols-4 gap-4 text-center pt-4 border-t border-gray-700/50">
        <div>
          <div className="text-2xl font-bold text-cyan-500">100k+</div>
          <div className="text-xs text-gray-400">Daily Reach</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-green-500">94%</div>
          <div className="text-xs text-gray-400">Open Rate</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-purple-500">5</div>
          <div className="text-xs text-gray-400">Active Alerts</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-orange-500">47</div>
          <div className="text-xs text-gray-400">This Month</div>
        </div>
      </div>
    </div>
  );
};

export default PublicAlertSystem;